import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { XMarkIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import InterviewAnalysisTranscript from '../../pages/screenings/InterviewAnalysisTranscript';

/**
 * Public hiring-manager interview replay.
 *
 * Fetches the session through the share-code-scoped public endpoint and renders
 * the recruiter's own Analysis & Transcript body (InterviewAnalysisTranscript,
 * extracted from ScreeningDetailPage) inside modal chrome that copies the
 * recruiter modal's header, tab bar and scroll container classes.
 *
 * This replaced a wrapper around pages/results/components/InterviewSessionModal.
 * That component is a SECOND, independently-written interview modal — the
 * recruiter screening page never used it — so the public page's question
 * analysis, transcript rendering and layout had all drifted from what the
 * recruiter actually sees. Pointing this modal at the recruiter's own markup is
 * what makes the two surfaces identical, and keeps InterviewSessionModal (and
 * its other live consumers: Profile, CandidateLibrary, the internal dashboards)
 * completely untouched.
 *
 * Two deliberate differences from the recruiter modal, both agreed:
 *   - No Proctoring Details tab. Integrity telemetry is recruiter-only; the
 *     public assessment modal already omits it for the same reason.
 *   - No "Translate to English" button. That endpoint is auth-only with no
 *     share-code equivalent, so it could only ever 401. The Original/English
 *     TOGGLE is kept — it is pure client-side rendering of a translation a
 *     recruiter already stored.
 */

/* ───── Normalize text (for translation lookup) ───── */
const normalizeText = (s) =>
  (s || '')
    .replace(/[.,/#!$%^&*;:{}=\-_`~()?]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const rawTurnText = (turn) =>
  typeof turn?.text === 'string' ? turn.text : (turn?.text?.text || turn?.content || turn?.message || '');

export default function PublicInterviewReplayModal({
  isOpen,
  onClose,
  shareCode,
  candidate,
  interviewTitle,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const [innerTab, setInnerTab] = useState('questions');
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  // 'hi' means "show Original", 'en' means "show English" — same literal codes the
  // recruiter modal uses, so the toggle behaves identically.
  const [displayLang, setDisplayLang] = useState('hi');

  useEffect(() => {
    if (!isOpen || !candidate?._id) return undefined;
    let cancelled = false;
    // Reset per-open so a second candidate never inherits the first one's
    // recording URL, expanded rows or language choice.
    setInnerTab('questions');
    setExpandedQuestions(new Set());
    setRecordingUrl(null);
    setDisplayLang('hi');
    (async () => {
      setLoading(true);
      setError('');
      setSession(null);
      try {
        const res = await axios.get(
          `/api/screenings/public/candidate-review/${shareCode}/interview/${candidate._id}/replay`
        );
        if (!cancelled) setSession({ ...res.data.session, shareCode });
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || 'Failed to load interview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, shareCode, candidate?._id]);

  const interviewLanguage = useMemo(() => {
    const raw = session?.audioConfig?.language;
    if (typeof raw !== 'string') return 'en';
    const v = raw.trim().toLowerCase();
    if (v === 'hindi') return 'hi';
    if (v === 'hinglish') return 'hi-en';
    if (v === 'english') return 'en';
    return v || 'en';
  }, [session?.audioConfig?.language]);

  const transcriptEnglish = Array.isArray(session?.transcriptEnglish) ? session.transcriptEnglish : null;
  const hasStoredTranslation =
    session?.transcriptEnglishStatus === 'ready' && transcriptEnglish && transcriptEnglish.length > 0;
  const showLanguageToggle = interviewLanguage !== 'en' && hasStoredTranslation;
  const showEnglish = showLanguageToggle && displayLang === 'en';

  // Normalized original-text → English lookup, matching the recruiter modal: keyed
  // on the source text rather than array position, so it survives any reshaping of
  // the transcript between storage and render.
  const englishByOriginal = useMemo(() => {
    const m = new Map();
    if (!transcriptEnglish) return m;
    for (const turn of transcriptEnglish) {
      const orig = turn?.metadata?.originalText;
      const eng = turn?.text;
      if (typeof orig === 'string' && typeof eng === 'string') {
        const key = normalizeText(orig);
        if (key) m.set(key, eng);
      }
    }
    return m;
  }, [transcriptEnglish]);

  const translateText = (str) => {
    if (!showEnglish || !hasStoredTranslation) return str;
    return englishByOriginal.get(normalizeText(str)) || str;
  };
  const pickTurnText = (turn) => translateText(rawTurnText(turn));

  const handleFetchRecording = async () => {
    const sessionId = session?.id || session?._id;
    if (!sessionId) return;
    setRecordingLoading(true);
    try {
      const res = await axios.get(
        `/api/screenings/public/candidate-review/${shareCode}/interview-session/${sessionId}/recording-url`
      );
      setRecordingUrl(res.data.url);
    } catch (err) {
      if (err.response?.status === 202) {
        toast('Recording is still being processed. Try again shortly.');
      } else {
        toast.error('Could not load recording');
      }
    } finally {
      setRecordingLoading(false);
    }
  };

  if (!isOpen) return null;

  const resolvedTitle = interviewTitle || session?.interview?.title || '';

  return (
    // Chrome classes copied from the recruiter modal in ScreeningDetailPage.
    <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] overflow-y-auto h-full w-full z-[110]">
      <div className="relative top-5 mx-auto p-0 border w-11/12 md:w-5/6 lg:w-4/5 xl:w-3/4 shadow-xl rounded-lg bg-white max-h-[95vh] overflow-hidden flex flex-col">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 select-none">
                Interview Replay &amp; Analysis
              </h3>
              <p className="mt-1 text-gray-600 text-sm">
                {session?.candidateName || candidate?.name || 'Anonymous Candidate'}
                {resolvedTitle ? <> &nbsp;•&nbsp; {resolvedTitle}</> : null}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {showLanguageToggle && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">View</span>
                  <div className="inline-flex items-center rounded-md border border-gray-200 bg-white p-0.5 h-7" role="group" aria-label="View language">
                    <button
                      type="button"
                      onClick={() => setDisplayLang('hi')}
                      className={`inline-flex items-center justify-center rounded font-medium transition-colors px-2 text-xs ${displayLang === 'hi' ? 'bg-[#007BFF] text-white' : 'text-gray-600 hover:text-gray-900'}`}
                      aria-pressed={displayLang === 'hi'}
                    >Original</button>
                    <button
                      type="button"
                      onClick={() => setDisplayLang('en')}
                      className={`inline-flex items-center justify-center rounded font-medium transition-colors px-2 text-xs ${displayLang === 'en' ? 'bg-[#007BFF] text-white' : 'text-gray-600 hover:text-gray-900'}`}
                      aria-pressed={displayLang === 'en'}
                    >English</button>
                  </div>
                </div>
              )}
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs Navigation — single tab, styled as the recruiter's so the layout
            rhythm is preserved with Proctoring Details omitted. */}
        <div className="border-b border-gray-200 bg-gray-50 px-6 flex items-center justify-between gap-4">
          <nav className="flex space-x-8 -mb-px">
            <span className="py-4 px-1 border-b-2 border-indigo-500 text-indigo-600 font-medium text-sm flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5" />
              Analysis &amp; Transcript
            </span>
          </nav>
        </div>

        {/* Tab Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              <p className="mt-3 text-sm text-gray-500">Loading interview…</p>
            </div>
          )}
          {!loading && error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
              {error}
            </div>
          )}
          {!loading && !error && session && (
            <InterviewAnalysisTranscript
              session={session}
              analysisSession={session}
              candidateNameFallback={candidate?.name || ''}
              interviewTitleFallback={resolvedTitle}
              recordingUrl={recordingUrl}
              recordingLoading={recordingLoading}
              onFetchRecording={handleFetchRecording}
              innerTab={innerTab}
              onInnerTabChange={setInnerTab}
              expandedQuestions={expandedQuestions}
              onExpandedQuestionsChange={setExpandedQuestions}
              translateText={translateText}
              pickTurnText={pickTurnText}
            />
          )}
        </div>
      </div>
    </div>
  );
}
